import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InvButton from '../../../components/InvButton';
import StatusChip from '../../../components/StatusChip';
import { getWardReturn, getWardReturnItems } from '../../../api/pharmacy';
import { formatDateTime, formatQty } from '../../../utils';
import { mapWardReturn, mapWardReturnItem } from '../../../utils/pharmacyMappers';
import { formatWardReturnError } from '../../../utils/wardReturnEntity';
import { IPD_COLORS, cardSx } from '../../../theme';

const HEADERS = ['Medicine', 'Batch', 'Requested', 'Accepted', 'Rejected', 'Reason'];

const headCell = {
  color: IPD_COLORS.textSecondary,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

/**
 * Read-only view of a ward return and its lines. Uses the same two endpoints the
 * acknowledge flow already calls, so processed returns can be inspected without
 * opening a decision screen that would no longer apply to them.
 */
export default function WardReturnDetailDialog({ open, row, onClose, resolveItemId }) {
  const [header, setHeader] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!open || !row?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setHeader(row);
    setLines([]);
    (async () => {
      try {
        const [detail, itemsPayload] = await Promise.all([
          getWardReturn(row.id),
          getWardReturnItems(row.id),
        ]);
        if (cancelled) return;
        setHeader(mapWardReturn(detail) || row);
        setLines((Array.isArray(itemsPayload) ? itemsPayload : []).map(mapWardReturnItem).filter(Boolean));
      } catch (e) {
        if (!cancelled) setError(formatWardReturnError(e, 'Failed to load return details'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, row, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { ...cardSx, p: 1 } }}>
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box component="span" sx={{ flex: 1 }}>
          Ward Return
          {header?.returnId ? ` — ${header.returnId}` : ''}
        </Box>
        {header?.status && <StatusChip status={header.status} />}
      </DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Alert
            severity="error"
            sx={{ borderRadius: '10px', my: 1 }}
            action={
              <InvButton variant="outlined" tone="teal" size="small" startIcon={<RefreshIcon />} onClick={retry}>
                Retry
              </InvButton>
            }
          >
            {error}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary, mb: 2 }}>
              Ward {header?.ward && header.ward !== '—' ? header.ward : 'Not Assigned'}
              {header?.patient_id && header.patient_id !== '—' ? ` · Patient ${header.patient_id}` : ''}
              {header?.admission_id && header.admission_id !== '—' ? ` · ${header.admission_id}` : ''}
              {header?.date ? ` · ${formatDateTime(header.date)}` : ''}
            </Typography>
            {header?.remarks && (
              <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary, mb: 2 }}>
                Remarks: {header.remarks}
              </Typography>
            )}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} sx={{ color: IPD_COLORS.primary }} />
              </Box>
            ) : !lines.length ? (
              <Typography color="text.secondary">No line items on this return.</Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table
                  size="small"
                  aria-label="Ward return line items"
                  sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}
                >
                  <TableHead>
                    <TableRow>
                      {HEADERS.map((h) => <TableCell key={h} sx={headCell}>{h}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>
                          {resolveItemId?.(line.item_id) || line.item_id}
                        </TableCell>
                        <TableCell>{line.batch_number}</TableCell>
                        <TableCell>{formatQty(line.requested_quantity)}</TableCell>
                        <TableCell>{formatQty(line.accepted_quantity)}</TableCell>
                        <TableCell>{formatQty(line.rejected_quantity)}</TableCell>
                        <TableCell sx={{ color: IPD_COLORS.textSecondary }}>
                          {line.rejection_reason || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <InvButton variant="outlined" onClick={onClose}>Close</InvButton>
      </DialogActions>
    </Dialog>
  );
}
